import { useParams, Navigate, Link } from "react-router-dom";
import InfoLayout from "@/components/InfoLayout";
import InlineCTA from "@/components/InlineCTA";
import { INSURANCES, COMPANY, SITE_URL } from "@/seo/data";

export default function VersicherungPage() {
  const { insuranceSlug } = useParams();
  const ins = INSURANCES.find((i) => i.slug === insuranceSlug);
  if (!ins) return <Navigate to="/versicherungen" replace />;

  const title = `${ins.name} Glasschaden melden | Direktabwicklung Autoglas`;
  const description = `${ins.name} Glasschaden bequem online melden: Direktabwicklung, Selbstbeteiligung erklärt, Termin in 24 h für Mannheim, Ludwigshafen und Rhein-Neckar.`;
  const canonical = `${SITE_URL}/versicherung/${ins.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `${ins.name} Glasschaden melden — Schritt für Schritt`,
    description,
    step: [
      { "@type": "HowToStep", name: "Schaden dokumentieren", text: "Fotos vom Schaden, Fahrzeugschein und Versicherungsschein bereitlegen." },
      { "@type": "HowToStep", name: "Online-Assistent ausfüllen", text: "Fahrzeugdaten und Schadensart im Schaden-Assistenten eintragen." },
      { "@type": "HowToStep", name: `${ins.name} als Versicherer wählen`, text: `Im Schritt 'Versicherung' ${ins.name} auswählen und Selbstbeteiligung angeben.` },
      { "@type": "HowToStep", name: "Abtretung digital unterzeichnen", text: "Die digitale Abtretungserklärung ermöglicht uns die Direktabwicklung." },
      { "@type": "HowToStep", name: "Termin wählen", text: "Freien Slot wählen — Reparatur oder Tausch erfolgt meist innerhalb von 24 Stunden." },
    ],
  };

  return (
    <InfoLayout title={title} description={description} canonical={canonical} jsonLd={jsonLd}>
      <h1>{ins.name} Glasschaden melden — Direktabwicklung in 5 Minuten</h1>
      <p className="lead">
        Sie sind bei der {ins.name} versichert und haben einen Steinschlag oder Glasschaden an Ihrem Fahrzeug?
        Wir übernehmen die komplette Schadensabwicklung mit der {ins.name} direkt — Sie melden den Schaden
        einmal online, wir kümmern uns um den Rest. Termin in der Metropolregion Rhein-Neckar (Mannheim,
        Ludwigshafen, Heidelberg und Umgebung) innerhalb von 24 Stunden.
      </p>

      <h2>So melden Sie Ihren {ins.short}-Glasschaden bei uns</h2>
      <ol>
        <li><strong>Fotos & Daten hochladen:</strong> Schadensfotos, Fahrzeugschein und {ins.short}-Versicherungsschein in unseren Schaden-Assistenten laden.</li>
        <li><strong>Versicherung auswählen:</strong> In Schritt 4 wählen Sie <em>{ins.name}</em> als Ihre Versicherung und tragen die vereinbarte Selbstbeteiligung ein.</li>
        <li><strong>Digitale Abtretungserklärung unterschreiben:</strong> Damit dürfen wir mit der {ins.name} direkt korrespondieren — Sie müssen nirgendwo anrufen.</li>
        <li><strong>Termin wählen & Reparatur durchführen:</strong> Wir holen Schadennummer und Kostenübernahme ein, reparieren oder tauschen die Scheibe.</li>
        <li><strong>Rechnung direkt an {ins.short}:</strong> Wir stellen die Rechnung der {ins.name} — Sie zahlen nur die Selbstbeteiligung (sofern vereinbart).</li>
      </ol>

      <h2>Was die {ins.name} bei Glasschäden übernimmt</h2>
      <p>{ins.note}</p>
      <p>
        Generell gilt: In der <strong>Teilkasko</strong> der {ins.name} sind <em>Steinschlagreparaturen</em>
        sowie der <em>Komplettaustausch</em> von Front-, Seiten- und Heckscheibe enthalten. Bei reiner
        Reparatur verzichtet die {ins.name} — wie die meisten Versicherer — in der Regel auf die
        Selbstbeteiligung, weil Reparatur erheblich günstiger ist als ein Tausch. Bei einem Komplettaustausch
        fällt die vereinbarte Selbstbeteiligung (üblich: 150 €) an.
      </p>

      <h2>Welche Unterlagen benötigt die {ins.name}?</h2>
      <ul>
        <li>Fahrzeugschein (Vorderseite) — Fahrzeug- und Halterdaten</li>
        <li>{ins.short}-Versicherungsschein oder Versicherungskarte — für die Vertragsnummer</li>
        <li>Fotos vom Schaden (Detail und Übersicht)</li>
        <li>Schadenbeschreibung mit Datum und Ort des Vorfalls</li>
        <li>Abtretungserklärung (digital signiert in unserem Schaden-Assistenten)</li>
        <li>Rechnung der Werkstatt — wird von uns automatisch erstellt</li>
      </ul>
      <p>
        Alle diese Unterlagen reichen wir elektronisch bei der {ins.name} ein. Eine separate Schadensmeldung
        Ihrerseits ist nicht erforderlich — die Abtretungserklärung berechtigt uns zur kompletten Abwicklung.
      </p>

      <h2>{ins.short} Schadenfreiheitsrabatt bei Glasschaden — bleibt der erhalten?</h2>
      <p>
        Ja. Glasschäden in der Teilkasko der {ins.name} sind SF-neutral und führen <strong>nicht</strong> zur
        Höherstufung Ihres Beitrags. Auch wenn Sie bereits mehrere Glasschäden im Jahr hatten, bleibt Ihr
        Schadenfreiheitsrabatt in der Vollkasko-Linie erhalten — Glas wird separat geführt.
      </p>

      <h2>Termin sofort buchen — wir wickeln {ins.name} ab</h2>
      <InlineCTA />

      <h2>Welche {ins.short}-Tarife sind betroffen?</h2>
      <p>
        Glasschäden sind <strong>standardmäßig in jeder Teilkasko-Versicherung</strong> der {ins.name}
        enthalten — unabhängig vom konkreten Tarif (Komfort, Premium, Smart, etc.). Auch in der Vollkasko
        sind Glasschäden mitversichert; dort kommen zusätzlich selbstverschuldete Schäden hinzu. Sind Sie
        nur haftpflichtversichert (Haftpflicht ohne Kasko), zahlt die {ins.name} keinen eigenen Glasschaden —
        in dem Fall können Sie jedoch unseren <Link to="/selbstzahler">Selbstzahler-Service</Link> nutzen.
      </p>

      <h2>Häufige Fragen zur {ins.name} Glasschaden-Abwicklung</h2>
      <h3>Muss ich die {ins.name} vorher anrufen?</h3>
      <p>
        Nein, das ist nicht nötig. Mit der digitalen Abtretungserklärung übernehmen wir die komplette
        Kommunikation. Sie sparen sich Telefonwarteschleifen und müssen sich um nichts kümmern.
      </p>
      <h3>Wie lange dauert die Kostenübernahme durch die {ins.name}?</h3>
      <p>
        Die Schadennummer erhalten wir meist innerhalb weniger Stunden. Die endgültige Kostenübernahme erfolgt
        nach Vorlage von Schadenfotos und Rechnung — in der Regel binnen 5–10 Werktagen. Sie zahlen während
        dieser Zeit nichts — wir gehen in Vorleistung.
      </p>
      <h3>Welche Selbstbeteiligung gilt bei der {ins.name}?</h3>
      <p>
        Das hängt von Ihrem konkreten Vertrag ab. Üblich sind 0 € (Reparatur), 150 € oder 300 € beim
        Komplettaustausch. Ihre genaue Selbstbeteiligung steht im Versicherungsschein — unsere KI liest sie
        auf Wunsch automatisch aus.
      </p>

      <h2>Andere Versicherungen</h2>
      <p className="text-sm">
        {INSURANCES.filter((i) => i.slug !== ins.slug).map((i, idx, arr) => (
          <span key={i.slug}>
            <Link to={`/versicherung/${i.slug}`}>{i.name} Glasschaden</Link>{idx < arr.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>
    </InfoLayout>
  );
}
